import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { RichText, SurfaceCard, ToggleSwitch } from '@guryou/html-components';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import SearchIcon from '@material-ui/icons/Search';
import ViewListIcon from '@material-ui/icons/ViewList';
import ViewModuleIcon from '@material-ui/icons/ViewModule';
import { dealTokens } from 'components/shared';

import { FilterPopoverButton } from '../../../../components/navigation/HeaderZones';
import ActionButton from '../../../../components/shared/ActionButton';

// Shared visual language for the two Studio hub pages — Fit Library's StudioHub
// and Fit Plans' PlansStudioHub — so both dashboards read as the same product
// surface. Keep additions here presentational-only: no data fetching, no routing.

const C = {
  textLight: dealTokens.color.textSubtle,
};

// The grid/list toggle used on every Performance Hub catalogue page (Exercises,
// Workouts, Circuits, Equipment, Meals, Recipes, Ingredients, Plans, Microcycles,
// Athletes, Templates…) so switching views feels identical everywhere. `border`/
// `primary`/`textMuted` take each page's own color tokens so it stays visually
// consistent with the page's existing palette. `labels` optionally overrides the
// two tooltip strings (defaults are plain English if a page has no `t()` in scope).
// `extraModes` (optional, default none) appends further { mode, Icon, label } entries
// after grid/list for pages that need a third presentation (e.g. Athletes' Leaderboard)
// without changing behavior for every other existing caller.
// `showLabels` (optional, default false) prints each mode's label beside its icon, for
// switches whose modes are not self-explanatory icons (Acquisti's "Per prodotto" /
// "Per cliente"); icon-only callers are unchanged.
export const ViewModeToggle = ({
  viewMode,
  onChange,
  border = dealTokens.color.border,
  primary = dealTokens.color.primary,
  textMuted = dealTokens.color.textMuted,
  labels = {},
  extraModes = [],
  modes: customModes,
  showLabels = false,
}) => {
  const modes = customModes || [
    { mode: 'grid', Icon: ViewModuleIcon, label: labels.grid || 'Grid view' },
    { mode: 'list', Icon: ViewListIcon, label: labels.list || 'List view' },
    ...extraModes,
  ];
  return (
    <div style={{ display: 'flex', border: `1px solid ${border}`, borderRadius: 8, overflow: 'hidden' }}>
      {modes.map(({ mode, Icon, label }, i) => (
        <button
          key={mode}
          type="button"
          onClick={() => onChange(mode)}
          title={label}
          aria-pressed={viewMode === mode}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            width: showLabels ? 'auto' : 34,
            padding: showLabels ? '0 12px' : 0,
            fontSize: 12.5,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            height: 34,
            border: 'none',
            borderRight: i < modes.length - 1 ? `1px solid ${border}` : 'none',
            backgroundColor: viewMode === mode ? primary : 'transparent',
            color: viewMode === mode ? dealTokens.color.surface : textMuted,
            cursor: 'pointer',
            transition: 'all 0.12s',
          }}
        >
          <Icon style={{ fontSize: 17 }} />
          {showLabels && label}
        </button>
      ))}
    </div>
  );
};

// The one search+filter+view-toggle bar used on every Performance Hub catalogue page —
// search, the "Filtri" popover and the results count (the "narrow the data" controls,
// scanned first) on the left; ViewModeToggle and actions (the "how to see it"/"do
// something" controls) on the right. Lives in-page (not portaled into the L3 SubHeader
// chrome) so it never risks being hidden behind TabBar's actions-zone overflow collapse.
// Each of `search`/`filters`/`viewMode`/`resultsCount`/`actions` is optional; a page
// passes only what it has, and passing none of them renders nothing.
// The bar itself gets a bordered, light-gray "well" so it reads as a distinct control
// strip rather than blending into the page background; the search input is white with
// its own border + icon so it stands out as the primary control inside that well.
// `filters.children` are the page's own filter controls, shown in the same
// FilterPopoverButton dropdown used by the legacy chrome-portaled HeaderFilters
// (components/navigation/HeaderZones.js) — one popover implementation, two homes.
// `flat` (default false) drops the bar's own border/radius/background/margin, leaving
// just a bottom divider — for when a caller (PageListView) nests this bar inside a
// larger bordered card sitting directly on top of a table, so the two read as one
// surface instead of two stacked cards.
export const PageToolbar = ({
  title,
  search,
  filters,
  viewMode,
  resultsCount,
  actions,
  border = dealTokens.color.border,
  primary = dealTokens.color.primary,
  textMuted = dealTokens.color.textMuted,
  cardBg = dealTokens.color.surface,
  background,
  flat,
}) => {
  if (!title && !search && !filters && !viewMode && !actions) {
    return null;
  }
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginBottom: flat ? 0 : 16,
        flexWrap: 'wrap',
        padding: '10px 14px',
        borderRadius: flat ? 0 : 10,
        border: flat ? 'none' : `1px solid ${border}`,
        // Explicit even when not flat: React writes object keys in order, so setting
        // `border` (shorthand) then leaving `borderBottom` as `undefined` makes React
        // clear that one longhand side (`element.style.borderBottom = ''`), silently
        // dropping just the bottom edge of an otherwise 4-sided border. Repeating the
        // same value here is a no-op for the non-flat case and the flat divider for
        // the flat one.
        borderBottom: `1px solid ${border}`,
        backgroundColor: flat ? 'transparent' : background || cardBg,
      }}
    >
      {/* Left zone: title → search → filtri popover → count — the controls that narrow the data. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
        {title && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {title}
          </span>
        )}
        {search && (
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <SearchIcon style={{ position: 'absolute', left: 10, fontSize: 16, color: textMuted, pointerEvents: 'none' }} />
            <input
              value={search.value}
              onChange={search.onChange}
              placeholder={search.placeholder}
              autoFocus={search.autoFocus}
              style={{
                width: 200,
                height: 36,
                padding: '7px 12px 7px 32px',
                border: `1px solid ${border}`,
                borderRadius: 8,
                fontSize: 13,
                color: dealTokens.color.text,
                backgroundColor: cardBg,
                outline: 'none',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
                transition: 'border-color 0.15s, box-shadow 0.15s',
              }}
              onFocus={e => {
                e.currentTarget.style.borderColor = primary;
                e.currentTarget.style.boxShadow = `0 0 0 3px ${primary}1A`;
              }}
              onBlur={e => {
                e.currentTarget.style.borderColor = border;
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </div>
        )}
        {filters && (
          <FilterPopoverButton count={filters.count} label={filters.label} compact={filters.compact} closeOnChange={filters.closeOnChange}>
            {filters.children}
          </FilterPopoverButton>
        )}
        {resultsCount && (
          <span style={{ fontSize: 12, color: textMuted, whiteSpace: 'nowrap' }}>
            {resultsCount.shown} / {resultsCount.total}
          </span>
        )}
      </div>

      {/* Right zone: view toggle → actions — the "how to see it"/"do something" controls. */}
      {(viewMode || actions) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {viewMode && <ViewModeToggle {...viewMode} border={border} primary={primary} textMuted={textMuted} />}
          {viewMode && actions && <Sep border={border} height={20} />}
          {actions}
        </div>
      )}
    </div>
  );
};

// Compact catalogue footer: range summary on the left, page navigation on the
// right. Use `attached` when it sits inside a bordered table container; grid
// views use the standalone bordered variant.
export const PagePagination = ({
  currentPage,
  totalPages,
  startIndex,
  endIndex,
  totalItems,
  onPageChange,
  border = dealTokens.color.border,
  primary = dealTokens.color.primary,
  textMuted = dealTokens.color.textMuted,
  cardBg = dealTokens.color.surface,
  attached,
}) => {
  const { t } = useTranslation();

  const pages = [];
  for (let page = 1; page <= totalPages; page += 1) {
    if (page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1) {
      pages.push(page);
    } else if (pages[pages.length - 1] !== '…') {
      pages.push('…');
    }
  }

  const buttonStyle = active => ({
    minWidth: 32,
    height: 32,
    padding: '0 9px',
    borderRadius: 7,
    border: `1px solid ${active ? primary : border}`,
    background: active ? primary : cardBg,
    color: active ? dealTokens.color.surface : textMuted,
    fontSize: 12.5,
    fontWeight: active ? 700 : 600,
    cursor: 'pointer',
  });

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        flexWrap: 'wrap',
        padding: '10px 14px',
        marginTop: attached ? 0 : 12,
        border: attached ? 'none' : `1px solid ${border}`,
        borderTop: `1px solid ${border}`,
        borderRadius: attached ? 0 : 10,
        background: cardBg,
      }}
    >
      <span style={{ color: textMuted, fontSize: 12.5 }}>
        {t('STUDIO2.paginationResults', '{{start}}–{{end}} of {{total}} results', {
          start: startIndex + 1,
          end: Math.min(endIndex, totalItems),
          total: totalItems,
        })}
      </span>
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            type="button"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            style={{ ...buttonStyle(false), opacity: currentPage === 1 ? 0.45 : 1 }}
          >
            ‹
          </button>
          {pages.map((page, index) =>
            page === '…' ? (
              <span key={`ellipsis-${index}`} style={{ color: textMuted, minWidth: 22, textAlign: 'center' }}>
                …
              </span>
            ) : (
              <button key={page} type="button" onClick={() => onPageChange(page)} style={buttonStyle(page === currentPage)}>
                {page}
              </button>
            )
          )}
          <button
            type="button"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={{ ...buttonStyle(false), opacity: currentPage === totalPages ? 0.45 : 1 }}
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
};

// The one table shell used by every Performance Hub roster/leaderboard-style page
// (Athletes, Coaches) for its "list"/"leaderboard" view modes — owns the toolbar
// (search/filters/view toggle), the table itself (columns → colgroup + thead,
// row wrapper with hover/click/last-row-border handling) and pagination as a
// single unit, replacing the near-identical ListContainer+table+pagination()
// trio that was copy-pasted per page (was: AthletesView's ListTable/RankingTable,
// CoachesLeaderboardView's CoachListTable/CoachRankingTable). A page now only
// supplies its `columns` (label/width/ariaLabel) and a `renderRow` returning one
// cell value per column — no more per-page table/row styled-components.
// `grid` is an escape hatch for view modes that aren't a table at all (card
// grids) — passed through untouched, sharing the same toolbar and pagination.
export const PageListView = ({
  title,
  search,
  filters,
  viewMode,
  actions,
  resultsCount,
  border = dealTokens.color.border,
  primary = dealTokens.color.primary,
  textMuted = dealTokens.color.textMuted,
  cardBg = dealTokens.color.surface,
  hoverBg = dealTokens.color.surface,
  grid,
  loading,
  loadingNode,
  columns,
  tableMinWidth,
  rows,
  rowKey,
  renderRow,
  onRowClick,
  emptyNode,
  page,
}) => {
  const paginationNode =
    page && page.totalItems > 0 ? (
      <PagePagination
        currentPage={page.currentPage}
        totalPages={page.totalPages}
        onPageChange={page.onPageChange}
        startIndex={page.startIndex}
        endIndex={page.endIndex}
        totalItems={page.totalItems}
        border={border}
        primary={primary}
        textMuted={textMuted}
        cardBg={cardBg}
        attached
      />
    ) : null;

  const toolbar = flat => (
    <PageToolbar
      title={title}
      search={search}
      filters={filters}
      viewMode={viewMode}
      actions={actions}
      resultsCount={resultsCount}
      border={border}
      primary={primary}
      textMuted={textMuted}
      cardBg={cardBg}
      flat={flat}
    />
  );

  // `loading` merges the toolbar with `loadingNode` the same way as the grid/table
  // case below — callers must pass a `loadingNode` with its own card chrome (border/
  // radius/background) stripped via className, since this card already provides it
  // (see AthletesView/CoachesLeaderboardView's `!border-0 !rounded-none !bg-transparent`
  // SkeletonLoader override). The empty state is the one case left unmerged: there's no
  // content to sit under the toolbar, and `emptyNode` is often its own standalone card
  // (e.g. Coaches' EmptyState) rather than something meant to nest inside another one.
  if (loading) {
    return (
      <div style={{ background: cardBg, border: `1px solid ${border}`, borderRadius: 12 }}>
        {toolbar(true)}
        <div style={{ overflow: 'hidden', borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }}>{loadingNode}</div>
      </div>
    );
  }

  if (!grid && rows.length === 0) {
    return (
      <>
        {toolbar(false)}
        {emptyNode}
      </>
    );
  }

  // The overflow: hidden that clips content into the card's rounded bottom corners lives on
  // this inner wrapper, not the outer card — the outer card also contains the toolbar, and
  // the Filtri popover it renders is absolutely positioned, so clipping at the outer level
  // cut it off whenever grid/rows was short (a small `emptyNode`/`noResultsNode`, e.g.). The
  // toolbar itself doesn't need clipping: it's `flat` (background: transparent) here, so it
  // has no square corners of its own to round off.
  return (
    <div style={{ background: cardBg, border: `1px solid ${border}`, borderRadius: 12 }}>
      {toolbar(true)}
      <div style={{ overflowX: tableMinWidth ? 'auto' : 'hidden', borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }}>
        {grid ? (
          <div style={{ padding: 16 }}>{grid}</div>
        ) : (
          <table style={{ width: '100%', minWidth: tableMinWidth, borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
              {columns.map((col, i) => (
                <col key={i} style={col.width ? { width: col.width } : undefined} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {columns.map((col, i) => (
                  <th
                    key={i}
                    aria-label={col.ariaLabel}
                    style={{
                      padding: '9px 12px',
                      paddingLeft: i === 0 ? 16 : undefined,
                      paddingRight: i === columns.length - 1 ? 16 : undefined,
                      background: dealTokens.color.surface,
                      color: textMuted,
                      fontSize: 10.5,
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      textAlign: 'left',
                      textTransform: 'uppercase',
                      borderBottom: `1px solid ${border}`,
                    }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i, arr) => {
                const isLast = i === arr.length - 1;
                const cells = renderRow(row, i, arr);
                return (
                  <tr
                    key={rowKey(row, i)}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    style={{ cursor: onRowClick ? 'pointer' : 'default', transition: '0.15s' }}
                    onMouseEnter={e => (e.currentTarget.style.background = hoverBg)}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    {cells.map((cell, ci) => (
                      <td
                        key={ci}
                        style={{
                          padding: 12,
                          paddingLeft: ci === 0 ? 16 : undefined,
                          paddingRight: ci === cells.length - 1 ? 16 : undefined,
                          verticalAlign: 'middle',
                          borderBottom: isLast ? 'none' : `1px solid ${border}`,
                        }}
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {paginationNode}
      </div>
    </div>
  );
};

// Pill-shaped filter toggle used across every fitLibrary catalogue page (Workouts,
// WorkoutPlans, Equipment, Movements, Microcycles, Ingredients, Meals, Recipes,
// NutritionPlans) — was copy-pasted verbatim into all nine files before this
// extraction. Color tokens come in as props, same convention as ViewModeToggle,
// so each page keeps its own palette.
export const FilterChip = ({ active, onClick, children, primary, border, cardBg, textMuted }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      padding: '4px 12px',
      borderRadius: 999,
      border: `1.5px solid ${active ? primary : border}`,
      backgroundColor: active ? primary : cardBg,
      color: active ? dealTokens.color.surface : textMuted,
      fontSize: 12,
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.12s',
      whiteSpace: 'nowrap',
      lineHeight: '20px',
    }}
  >
    {children}
  </button>
);

// Multi-select pill group for AI-generation filters (difficulty/muscle groups) —
// extracted from ImportPanel.js so ExerciseBuilderForm's "Generate with AI"
// chooser step can reuse the exact same picker instead of re-implementing it.
// Renders with FilterChip, same token-props convention as everything else here.
export const ChipGroup = ({ title, options, value, onChange, maxSelect, primary, border, cardBg, textMuted }) => {
  const toggle = optValue => {
    if (value.includes(optValue)) {
      onChange(value.filter(v => v !== optValue));
    } else if (value.length < maxSelect) {
      onChange([...value, optValue]);
    }
  };
  return (
    <div>
      <MicroLabel textLight={textMuted}>{title}</MicroLabel>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {options.map(opt => (
          <FilterChip
            key={opt.value}
            active={value.includes(opt.value)}
            onClick={() => toggle(opt.value)}
            primary={primary}
            border={border}
            cardBg={cardBg}
            textMuted={textMuted}
          >
            {opt.label}
          </FilterChip>
        ))}
      </div>
    </div>
  );
};

// Vertical divider used to group filter/action clusters in a page's toolbar row.
export const Sep = ({ border, height = 20, alignSelf }) => <div style={{ width: 1, height, backgroundColor: border, flexShrink: 0, alignSelf }} />;

// Small colored status/variant pill — was duplicated verbatim in WorkoutsView and
// MovementsView. `variant` picks a semantic color from the token map; unknown/omitted
// variants fall back to `default`. Tokens come in as props like the other primitives.
// `icon` is an optional MUI icon component rendered before the label, for status
// badges where the shape should read even without relying on color alone (e.g. a
// list row's status tag) — omit it for the plain color-only pill used elsewhere.
export const Badge = ({ children, variant = 'default', icon: Icon, textMuted, success, warning, danger, info, accentDark }) => {
  const map = {
    default: { bg: dealTokens.color.canvas, text: textMuted },
    success: { bg: dealTokens.color.successSoft, text: success },
    warning: { bg: dealTokens.color.warningSoft, text: warning || dealTokens.color.warning },
    danger: { bg: dealTokens.color.dangerSoft, text: danger },
    info: { bg: dealTokens.color.infoSoft, text: info },
    accent: { bg: dealTokens.color.primarySoft, text: accentDark },
  };
  const s = map[variant] || map.default;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: Icon ? 4 : 0,
        borderRadius: 999,
        padding: '2px 10px',
        fontSize: 11,
        fontWeight: 600,
        backgroundColor: s.bg,
        color: s.text,
        whiteSpace: 'nowrap',
      }}
    >
      {Icon && <Icon style={{ fontSize: 12 }} />}
      {children}
    </span>
  );
};

export const SectionLabel = ({ children }) => (
  <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 14 }}>
    {children}
  </div>
);

// Entry point to a future AI agent that will manage content on a coach's behalf.
// None of these exist yet — every tile renders "Coming soon" and isn't clickable.
export const AgentCard = ({ icon, name, description }) => (
  <div
    style={{
      background: `linear-gradient(135deg, ${dealTokens.color.text} 0%, ${dealTokens.color.textMuted} 55%, ${dealTokens.color.info} 100%)`,
      borderRadius: 14,
      padding: '18px 20px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
    }}
  >
    <div
      style={{
        position: 'absolute',
        top: -28,
        right: -28,
        width: 110,
        height: 110,
        borderRadius: '50%',
        background: 'rgba(123,104,238,0.08)',
        pointerEvents: 'none',
      }}
    />

    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <div
        style={{
          width: 34,
          height: 34,
          borderRadius: 9,
          backgroundColor: 'rgba(123,104,238,0.18)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: dealTokens.color.info,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          fontSize: 9,
          fontWeight: 800,
          color: 'rgba(255,255,255,0.55)',
          background: 'rgba(255,255,255,0.08)',
          borderRadius: 999,
          padding: '3px 9px',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
        }}
      >
        <AccessTimeIcon style={{ fontSize: 11 }} /> Coming soon
      </span>
    </div>

    <div>
      <div style={{ fontSize: 14, fontWeight: 800, color: dealTokens.color.surface, marginBottom: 3, letterSpacing: '-0.2px' }}>{name}</div>
      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>{description}</div>
    </div>
  </div>
);

// Single inline row-action icon button — the 30×30/radius-7/15%-tint-background
// shape every catalogue page's Edit/Delete/Publish/etc. button already converged
// on independently (WorkoutPlansView, NutritionPlansView, WorkoutCardList) before
// this extraction; pulled out here so every OTHER page's action cluster reuses the
// exact same footprint instead of re-typing the same six style properties per
// button. `color` is the single token driving both the icon color and its 15%-
// opacity tinted background — pass `B.info` for Edit, `B.danger` for Delete, etc.
// so "edit is blue / delete is red" is guaranteed to render identically everywhere
// (Performance Hub-wide action-color audit, #1507 follow-up) rather than each page
// hand-rolling its own resting/hover color per action.
export const RowActionButton = ({ icon, title, onClick, color, disabled, size = 30 }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    style={{
      width: size,
      height: size,
      borderRadius: 7,
      border: 'none',
      backgroundColor: `${color}15`,
      color,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      flexShrink: 0,
    }}
  >
    {icon}
  </button>
);

// Compact row-actions overflow ("kebab") for list rows and toolbars (#1507 A-R8).
// Same 30×30 icon-button footprint as the plan lists' inline action buttons, so it
// slots into an existing action cluster as one more sibling. `actions` is
// [{ key, label, icon, onClick, danger?, info?, disabled? }] — `danger` renders red,
// `info` renders blue (same "edit is blue" convention as RowActionButton, for
// pages that route Edit through the overflow menu instead of its own inline
// button), anything else renders neutral ink/gray. A `null`/`false` entry is
// skipped so callers can build the list conditionally. Colors come in as tokens
// (`border`/`textMuted`/`danger`/`info`) exactly like ViewModeToggle, keeping the
// menu on each page's own palette. Presentational only — every action's behavior
// lives in the caller. Closes on pick, on outside click, and on Escape.
// The dropdown itself renders through a portal into document.body, positioned by the
// trigger button's own bounding rect, rather than `position: absolute` inside this
// component's own DOM subtree. Every row lives inside a table wrapper that needs its
// own `overflow: hidden` to clip the table into the card's rounded bottom corners
// (see PageListView's comment above) — an absolutely-positioned dropdown there gets
// clipped by that same overflow whenever the table is short enough that the menu
// would extend past the wrapper's bottom edge, making its lower items unreachable.
// Unlike the toolbar's Filtri popover (also absolutely positioned, fixed by scoping
// the overflow more narrowly so it excludes the toolbar), there's no equivalent
// narrower scope to carve out here — the clipping wrapper and the menu trigger are
// both inside the same table, so a portal is the only fix that doesn't fight itself.
export const RowActionsMenu = ({
  actions,
  title = 'More actions',
  border = dealTokens.color.border,
  textMuted = dealTokens.color.textMuted,
  danger = dealTokens.color.danger,
  info = dealTokens.color.info,
  size = 30,
  menuClassName,
  menuZIndex = 1300,
  menuWidth = 190,
}) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = e => {
      if (rootRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = e => e.key === 'Escape' && setOpen(false);
    // The menu is no longer a DOM descendant of this row once portaled, so it moves
    // with the page, not with a scrolling table body — close it on scroll/resize
    // rather than trying to keep re-measuring/repositioning a menu the user is
    // mid-scroll past or that a reflow just moved out from under.
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  // The portal fix above solved clipping by the table's own overflow, but a menu
  // opened from a row near the bottom of the viewport can now just as easily run
  // past the bottom of the WINDOW instead — `menuPos.top` is only ever computed from
  // the button's rect, with no awareness of the menu's own (dynamic, item-count-
  // dependent) height. Runs after the portal has actually painted at its first-guess
  // position, measures it for real, and flips it to open upward off the button (or
  // clamps it back onto screen horizontally) only if it doesn't fit — converges in at
  // most one extra layout pass since the corrected position no longer overflows.
  useLayoutEffect(() => {
    if (!open || !menuPos || !menuRef.current || !buttonRef.current) return;
    const menuRect = menuRef.current.getBoundingClientRect();
    const overflowsBottom = menuRect.bottom > window.innerHeight;
    const overflowsLeft = menuRect.left < 0;
    if (!overflowsBottom && !overflowsLeft) return;
    const buttonRect = buttonRef.current.getBoundingClientRect();
    setMenuPos(prev => ({
      top: overflowsBottom ? buttonRect.top - menuRect.height - 4 : prev.top,
      left: overflowsLeft ? 8 : prev.left,
    }));
  }, [open, menuPos]);

  const items = (actions || []).filter(Boolean);
  if (items.length === 0) return null;

  const handleToggle = e => {
    e.stopPropagation();
    setOpen(o => {
      const next = !o;
      if (next && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setMenuPos({ top: rect.bottom + 4, left: rect.right - menuWidth });
      }
      return next;
    });
  };

  return (
    <div ref={rootRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        ref={buttonRef}
        type="button"
        title={title}
        onClick={handleToggle}
        style={{
          width: size,
          height: size,
          borderRadius: 7,
          border: 'none',
          backgroundColor: open ? `${textMuted}22` : `${textMuted}12`,
          color: textMuted,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        <MoreVertIcon style={{ fontSize: Math.round(size / 2) }} />
      </button>
      {open &&
        menuPos &&
        createPortal(
          <div
            ref={menuRef}
            className={menuClassName}
            style={{
              position: 'fixed',
              top: menuPos.top,
              left: menuPos.left,
              zIndex: menuZIndex,
              minWidth: menuWidth,
              backgroundColor: dealTokens.color.surface,
              border: `1px solid ${border}`,
              borderRadius: 10,
              boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
              padding: 4,
            }}
          >
            {items.map(action => (
              <button
                key={action.key || action.label}
                type="button"
                disabled={action.disabled}
                onClick={e => {
                  e.stopPropagation();
                  setOpen(false);
                  action.onClick();
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  border: 'none',
                  background: 'transparent',
                  borderRadius: 7,
                  padding: '8px 10px',
                  fontSize: 12.5,
                  fontWeight: 600,
                  textAlign: 'left',
                  color: action.danger ? danger : action.info ? info : dealTokens.color.text,
                  cursor: action.disabled ? 'not-allowed' : 'pointer',
                  opacity: action.disabled ? 0.45 : 1,
                  whiteSpace: menuWidth > 190 ? 'normal' : 'nowrap',
                }}
                onMouseEnter={e => !action.disabled && (e.currentTarget.style.backgroundColor = dealTokens.color.canvas)}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <span style={{ display: 'inline-flex', color: action.danger ? danger : action.info ? info : textMuted }}>{action.icon}</span>
                {action.label}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
};

// Exercise/workout difficulty level — color + English label, duplicated verbatim
// across StudioHub, WorkoutLibraryDrawer, MovementLibraryDrawer, WorkoutCardList
// before this extraction. LEVEL_LABEL is plain English (not i18n) in all four
// original sources — left as-is here rather than fixing that gap in this pass.
export const LEVEL_COLOR = {
  beginner: dealTokens.color.success,
  intermediate: dealTokens.color.info,
  expert: dealTokens.color.warning,
  athlete: dealTokens.color.primary,
  all_levels: dealTokens.color.textSubtle,
};

export const LEVEL_LABEL = {
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  expert: 'Expert',
  athlete: 'Athlete',
  all_levels: 'All levels',
};

// Meal category → i18n key, duplicated verbatim between MealFormView and
// MealTemplateLibraryDrawer before this extraction.
export const MEAL_TYPE_KEY = {
  breakfast: 'mealCategoryBreakfast',
  morning_snack: 'categoryMorningSnack',
  pre_workout: 'mealCategoryPreWorkout',
  lunch: 'mealCategoryLunch',
  afternoon_snack: 'categoryAfternoonSnack',
  post_workout: 'mealCategoryPostWorkout',
  dinner: 'mealCategoryDinner',
  evening_snack: 'categoryEveningSnack',
  custom: 'categoryCustom',
};

// Catalogue-entity status chips (workout_plans.status et al.) — shared visual
// vocabulary for every Performance Hub list view. Extracted from the deleted
// fitLibrary/views/training/mockData.js fixture (issue #1498).
export const STATUS_META = {
  draft: { label: 'Draft', color: dealTokens.color.textMuted, bg: `${dealTokens.color.textMuted}18` },
  reviewed: { label: 'Reviewed', color: dealTokens.color.info, bg: `${dealTokens.color.info}18` },
  published: { label: 'Published', color: dealTokens.color.success, bg: `${dealTokens.color.success}18` },
  archived: { label: 'Archived', color: dealTokens.color.danger, bg: `${dealTokens.color.danger}18` },
};

// FlatCard — same background/border/radius/hover/selected/accentTop recipe as
// @guryou/html-components' SurfaceCard, minus the box-shadow. For Performance Hub
// surfaces that sit directly on an already-white page and don't need to visually
// "lift" off it (PlansStudioHub's KPI tiles, "Recent plans"/"Quick actions" section
// cards, clickable option rows) — flat border only, no shadow, unlike SurfaceCard's
// default soft lift used elsewhere in the app. A thin default-props wrapper, not a
// fork: every SurfaceCard prop (hoverable, selected, accentTop, padding, radius,
// onClick, background, borderColor, …) still works unchanged, and `styled(FlatCard)`
// still works as an extension point exactly like `styled(SurfaceCard)` did — a
// caller can still pass an explicit `shadow`/`hoverShadow` to opt back in per card.
export const FlatCard = props => <SurfaceCard shadow="none" hoverShadow="none" {...props} />;

// FlatKpiCard — same 3px top-accent KPI tile as @guryou/html-components' KpiCard
// (`accentTop` + 14px/16px padding), built on FlatCard instead of SurfaceCard so KPI
// strips read as flat too, matching every other Performance Hub card. KpiCard itself
// hardcodes SurfaceCard internally with no way to opt out of its shadow, so it can't
// just be passed `shadow="none"` — this is a parallel definition, not a wrapper.
export const FlatKpiCard = ({ accent = dealTokens.color.primary, ...props }) => <FlatCard accentTop={accent} padding="14px 16px" {...props} />;

// StatTile — semantic KPI tile shared by Reports 2 and Performance Hub. Colour
// communicates meaning rather than decoration: neutral is the default, primary
// identifies at most one headline metric per page, and the remaining roles are
// reserved for actual success/danger/warning states. The icon treatment matches
// Plancia: monochrome on a neutral canvas chip, never tinted by the semantic role.
// `color` is validated/normalized here but deliberately left NOT wired to any visible
// styling yet — which tiles actually get tinted is a product decision still to be made
// (owner call, 2026-09-14), not something to bake in ad hoc. Every value renders in
// plain ink for now regardless of `color`.
export const StatTile = ({ icon, label, value, color = 'neutral', meta, footer, style }) => {
  const semanticColors = ['neutral', 'primary', 'success', 'danger', 'warning'];
  const semanticColor = semanticColors.includes(color) ? color : 'neutral';

  // Stacked, not side-by-side: label + icon chip on top, the value on its own line, then
  // `meta` (e.g. a trend pill) and a hairline-separated `footer`. The old icon-left layout
  // spent ~50px of every tile on the icon column, so in a 6-up strip (Reports) labels and
  // trends wrapped and every tile came out a different height. The column is a flex
  // column with the footer pushed to the bottom (`marginTop: auto`), so tiles in one row
  // (grid rows stretch) line their footers up whatever the label length.
  return (
    <FlatCard
      radius={12}
      padding={16}
      style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0, height: '100%', boxSizing: 'border-box', ...style }}
    >
      <span style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
        <span
          title={typeof label === 'string' ? label : undefined}
          style={{
            minWidth: 0,
            fontSize: 12.5,
            fontWeight: dealTokens.font.weightMedium,
            lineHeight: 1.35,
            color: dealTokens.color.textMuted,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {label}
        </span>
        {icon && (
          <span
            data-semantic-color={semanticColor}
            style={{
              width: 30,
              height: 30,
              borderRadius: dealTokens.radius.md,
              backgroundColor: dealTokens.color.canvas,
              color: dealTokens.color.textMuted,
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            {icon}
          </span>
        )}
      </span>
      <strong
        style={{
          display: 'block',
          fontSize: 24,
          fontWeight: dealTokens.font.weightSemibold,
          lineHeight: 1.15,
          color: dealTokens.color.text,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value}
      </strong>
      {meta && <span style={{ display: 'block', fontSize: 11.5, color: dealTokens.color.textMuted }}>{meta}</span>}
      {footer && (
        <span
          style={{
            display: 'block',
            marginTop: 'auto',
            paddingTop: 10,
            borderTop: `1px solid ${dealTokens.color.border}`,
            fontSize: 11.5,
            color: dealTokens.color.textMuted,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {footer}
        </span>
      )}
    </FlatCard>
  );
};

// ─── Form primitives ───────────────────────────────────────────────────────────
// Card/CardHeader/Label/TextInput/Toggle below were copy-pasted verbatim across
// fitLibrary's create/edit form views before this extraction: ExerciseFormView,
// nutrition/MealFormView, nutrition/RecipeFormView (20-24px "full page" cards) and
// the builders/WorkoutBuilderForm + builders/CircuitBuilderForm pair (14-20px
// "compact" cards, used both as a route and embedded in a picker drawer). Same
// color-tokens-as-props convention as ViewModeToggle/FilterChip above.

// Card — bordered white card shell, radius 12, subtle shadow. Byte-identical
// across all five source files, so it needs no size variant.
export const Card = ({ children, style, cardBg, border }) => (
  <div
    style={{
      backgroundColor: cardBg,
      border: `1px solid ${border}`,
      borderRadius: 12,
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      ...style,
    }}
  >
    {children}
  </div>
);

// CardHeader — Card's title/description strip. Two sizes coexisted verbatim: the
// full-page form views use generous 20-24px padding + 15px title; the two builder
// forms use tighter 14-20px padding + 14px title. `compact` switches to the latter.
export const CardHeader = ({ title, description, border, textPrimary, textMuted, compact }) => (
  <div style={{ padding: compact ? '14px 20px' : '20px 24px 16px', borderBottom: `1px solid ${border}` }}>
    <h3 style={{ fontSize: compact ? 14 : 15, fontWeight: 700, color: textPrimary, margin: 0 }}>{title}</h3>
    {description && <p style={{ fontSize: compact ? 12 : 13, color: textMuted, margin: compact ? '3px 0 0' : '4px 0 0' }}>{description}</p>}
  </div>
);

// Label — block form-field label with an optional required asterisk. From
// ExerciseFormView / MealFormView / RecipeFormView (byte-identical).
export const Label = ({ children, required, textMuted, accent }) => (
  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: textMuted, marginBottom: 6 }}>
    {children}
    {required && <span style={{ color: accent, marginLeft: 3 }}>*</span>}
  </label>
);

// MicroLabel — uppercase micro-label above builder-form fields (no required
// support — callers spell out "Title *" inline instead). From WorkoutBuilderForm /
// CircuitBuilderForm (byte-identical). Deliberately a separate export from Label
// rather than a variant prop — the two have different content models (plain text
// vs. required-aware children).
export const MicroLabel = ({ children, textLight }) => (
  <div style={{ fontSize: 11, fontWeight: 700, color: textLight, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
    {children}
  </div>
);

// TextInput — text/textarea/number field with a focus-border highlight. Byte-
// identical between MealFormView and RecipeFormView (adds `type`/`min`/`step` for
// numeric fields). ExerciseFormView's near-variant (adds `required`, no
// type/min/step) is folded in here too — it's a strict superset, so both callers
// get exactly their previous behavior via the props they actually pass.
export const TextInput = ({ value, onChange, placeholder, type = 'text', required, multiline, min, step, border, primary, textPrimary, cardBg }) => {
  const base = {
    width: '100%',
    padding: '10px 14px',
    border: `1px solid ${border}`,
    borderRadius: 8,
    fontSize: 14,
    color: textPrimary,
    backgroundColor: cardBg,
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    transition: 'border-color 0.15s',
  };
  const focusOn = e => (e.currentTarget.style.borderColor = primary);
  const focusOff = e => (e.currentTarget.style.borderColor = border);
  return multiline ? (
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      rows={3}
      style={{ ...base, resize: 'vertical', lineHeight: 1.5 }}
      onFocus={focusOn}
      onBlur={focusOff}
    />
  ) : (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      min={min}
      step={step}
      style={base}
      onFocus={focusOn}
      onBlur={focusOff}
    />
  );
};

// RichTextField — wraps the legacy RichText (libs/html/components/src/forms/RichText.js,
// a react-draft-wysiwyg editor Form scrapes via a HiddenField) so its editor lines up
// with a full-width TextBox in the same Card. RichText spreads `{...props}` straight
// onto the underlying <Editor> as the LAST JSX attribute, so a caller's own
// `wrapperStyle`/`toolbarStyle`/`editorStyle` fully replace (not merge with) its
// internal defaults rather than extending them — passing just `width="100%"` (not
// even a prop RichText reads) had no effect. This reproduces RichText's own default
// `wrapperStyle` and adds `width: '100%'`/`boxSizing: 'border-box'` so the editor
// lines up with the field above it, and also overrides `toolbarStyle`/`editorStyle`
// to drop the toolbar's own nested `#f1f1f1` box-border and the react-draft-wysiwyg
// stylesheet's `.rdw-editor-wrapper > div` side padding (both from
// libs/html/components/src/assets/react-draft-wysiwyg.css / inputFields.css — global
// rules affecting every RichText instance, left untouched) — inline styles win over
// those class rules, so this only changes how RichTextField's own toolbar/editor
// divs look, replacing the nested border with a single bottom divider flush with the
// outer box. Doesn't touch RichText.js itself or any of its editing behavior.
export const RichTextField = props => (
  <RichText
    wrapperStyle={{
      border: '1px solid rgba(0,0,0,0.23)',
      borderRadius: 4,
      padding: 0,
      marginTop: 8,
      marginBottom: 4,
      width: '100%',
      boxSizing: 'border-box',
      overflow: 'hidden',
    }}
    toolbarStyle={{
      border: 'none',
      borderBottom: '1px solid rgba(0,0,0,0.23)',
      borderRadius: 0,
      margin: 0,
      padding: '8px 8px',
    }}
    editorStyle={{ minHeight: 120, padding: '10px 14px' }}
    {...props}
  />
);

// Toggle — small on/off switch (36×20 track). Byte-identical between
// WorkoutBuilderForm and CircuitBuilderForm. The 42×24 variants used by
// ExerciseFormView/RecipeFormView are a different size and have drifted from each
// other (RecipeFormView's adds a `hint` line) — left local rather than forced in
// here.
// Composes the shared ToggleSwitch (@guryou/html-components) with an inline label —
// kept as a thin wrapper so the ~20 existing call sites across Performance Hub don't
// need to change. Note: before this, any call site that omitted primary/border (e.g.
// ExerciseFormView.js's "Uses weights" toggle) rendered with an undefined background
// (invisible track) — ToggleSwitch's own defaults now cover that case too.
export const Toggle = ({ checked, onChange, label, primary, border, textPrimary }) => (
  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
    <ToggleSwitch checked={checked} onChange={onChange} primary={primary} border={border} />
    <span style={{ fontSize: 12, color: textPrimary }}>{label}</span>
  </label>
);

// PrimaryButton / GhostButton — filled and outlined action buttons, 8px radius.
// Replaces Coaching's parallel ".studio-primary"/".studio-ghost" MUI theme classes
// (libs/html/theme/src/overrides/button.js) so every Performance Hub button — Fit
// Library's inline-styled ones included — renders through the same visual recipe
// instead of two systems that could (and did) drift apart. Filled = commits an
// action (Save, Publish, Send); ghost = a lower-emphasis or reversible one (Cancel,
// Back, Disable). Icons pass as children alongside the label, same as Fit Library's
// inline buttons (e.g. `<PersonAddIcon .../> {label}`).
export const PrimaryButton = ({ children, disabled, onClick, type = 'button', primary: _primary, textLight: _textLight, style, ...rest }) => (
  <ActionButton type={type} variant="primary" size="md" onClick={onClick} disabled={disabled} style={style} {...rest}>
    {children}
  </ActionButton>
);

export const GhostButton = ({
  children,
  disabled,
  onClick,
  type = 'button',
  border: _border,
  textPrimary: _textPrimary,
  textLight: _textLight,
  style,
  ...rest
}) => (
  <ActionButton type={type} variant="ghost" size="md" onClick={onClick} disabled={disabled} style={style} {...rest}>
    {children}
  </ActionButton>
);
